/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { action, flow, makeObservable, observable } from 'mobx';
import type { GlobalStore } from '@roc/feature-app-core';
import type { ApiResponse } from '@roc/client-portal-shared/services';
import { ValuationPaymentService } from '../services/valuationPaymentService';
import { DEFAULT_LINK_ERROR, getApiErrorMessage } from '../modules/valuationPaymentModule/valuationPaymentUtils';

export interface ValuationInfo {
  loanId: number;
  propertyId: number | null;
  properties: any[];
  fee: number;
  cardKnoxUrl: string;
  pendingPayment: boolean;
  vendorName: string;
  hidePointOfContactStep?: boolean;
}

export interface PaymentPointOfContact {
  name: string;
  email: string;
  phoneNumber: string;
  propertyAccessInstructions: string;
}

const POINT_OF_CONTACT_PHONE_DIGITS = 10;

export const formatPointOfContactPhoneNumber = (value = '') => {
  const digits = value.replace(/\D/g, '').slice(0, POINT_OF_CONTACT_PHONE_DIGITS);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export class ValuationPaymentStore {
  private globalStore: GlobalStore;
  private valuationPaymentService: ValuationPaymentService;

  fee = 0;
  loading = true;
  /** Signed valuation payment link token from the URL; the backend derives the payment from it. */
  token: string = null;
  cardHolderName = '';
  paymentStatus = false;
  documentSignStatus = false;
  documentUrl: string = null;
  cardKnoxURL: string = null;
  valuationInfo: ValuationInfo = null;
  pendingPayment: boolean = null;
  loanPropertyValuationId: number = null;
  pointOfContact: PaymentPointOfContact = {
    name: '',
    email: '',
    phoneNumber: '',
    propertyAccessInstructions: '',
  };
  savingPointOfContact = false;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.valuationPaymentService = new ValuationPaymentService();

    makeObservable(this, {
      reset: action,
      setFee: action,
      fee: observable,
      setToken: action,
      setCardHolderName: action,
      setLoading: action,
      token: observable,
      cardHolderName: observable,
      loading: observable,
      generateDocument: flow,
      setDocumentUrl: action,
      setCardKnoxUrl: action,
      documentUrl: observable,
      fetchValuationInfo: flow,
      cardKnoxURL: observable,
      setValuationInfo: action,
      setPaymentStatus: action,
      valuationInfo: observable,
      paymentStatus: observable,
      setDocumentSignStatus: action,
      documentSignStatus: observable,
      setPendingPayment: action,
      pendingPayment: observable,
      pointOfContact: observable,
      savingPointOfContact: observable,
      setPointOfContact: action,
      setPointOfContactField: action,
      setSavingPointOfContact: action,
      savePointOfContact: flow,
    });
  }

  setLoading(value: boolean) {
    this.loading = value;
  }

  setValuationInfo(info: ValuationInfo) {
    this.valuationInfo = info;
  }

  setToken(token: string) {
    this.token = token;
  }

  setCardHolderName(name: string) {
    this.cardHolderName = name;
  }

  setFee(feeAmount: number) {
    this.fee = feeAmount;
  }

  setDocumentSignStatus(status: boolean) {
    this.documentSignStatus = status;
  }

  setPendingPayment(value: boolean) {
    this.pendingPayment = value;
  }

  setDocumentUrl(url: string) {
    this.documentUrl = url;
  }

  setCardKnoxUrl(url: string) {
    this.cardKnoxURL = url;
  }

  setPaymentStatus(status: boolean) {
    this.paymentStatus = status;
  }

  setPointOfContact(pointOfContact?: PaymentPointOfContact) {
    this.pointOfContact = {
      name: pointOfContact?.name || '',
      email: pointOfContact?.email || '',
      phoneNumber: formatPointOfContactPhoneNumber(pointOfContact?.phoneNumber || ''),
      propertyAccessInstructions: pointOfContact?.propertyAccessInstructions || '',
    };
  }

  setPointOfContactField(field: keyof PaymentPointOfContact, value: string) {
    this.pointOfContact = {
      ...this.pointOfContact,
      [field]: field === 'phoneNumber' ? formatPointOfContactPhoneNumber(value) : value,
    };
  }

  setSavingPointOfContact(value: boolean) {
    this.savingPointOfContact = value;
  }

  hasZeroAppraisalFee() {
    return this.valuationInfo?.properties?.some(property =>
      property?.addOns?.some(
        (addon: { type: string; addOnCost: number }) =>
          addon.type === 'Appraisal Fee' && addon.addOnCost === 0
      )
    ) ?? false;
  }

  getPropertyAddresses() {
    return this.valuationInfo?.properties
      ?.map(property => property?.address?.trim())
      .filter(Boolean)
      .join(' | ') || '';
  }

  *generateDocument(signatureDataURL: string) {
    const loanId = this.valuationInfo?.loanId;
    const cardHolderName = this.cardHolderName?.trim();
    const address = this.getPropertyAddresses();

    if (!cardHolderName || !address) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Missing valuation payment details. Please review the form and try again.',
      });
      this.setDocumentSignStatus(false);
      return false;
    }

    try {
      const response: ApiResponse = yield this.valuationPaymentService.generateValuationPaymentDocument(
        {
          loanId,
          cardHolderName,
          address,
          signature: signatureDataURL,
          userAgent: navigator.userAgent,
          loanPropertyValuationId: this.loanPropertyValuationId,
          vendorName: this.valuationInfo?.vendorName,
          token: this.token,
        }
      );

      const wasSigned = Boolean(response?.data?.data);

      this.setDocumentSignStatus(wasSigned);

      if (wasSigned) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Valuation payment authorization signed successfully!',
        });
        return true;
      }

      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while signing the valuation payment authorization.',
      });
      return false;
    } catch (e) {
      this.setDocumentSignStatus(false);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error while signing the valuation payment authorization.',
      });
      return false;
    }
  }

  *savePointOfContact() {
    const pointOfContact = {
      name: this.pointOfContact.name?.trim(),
      email: this.pointOfContact.email?.trim(),
      phoneNumber: formatPointOfContactPhoneNumber(this.pointOfContact.phoneNumber || '').trim(),
      propertyAccessInstructions: this.pointOfContact.propertyAccessInstructions?.trim() || '',
    };

    this.setSavingPointOfContact(true);

    try {
      yield this.valuationPaymentService.savePaymentPointOfContact({
        token: this.token,
        pointOfContact,
      });
      this.setPointOfContact(pointOfContact);
      return true;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'There was an error saving the point of contact.',
      });
      return false;
    } finally {
      this.setSavingPointOfContact(false);
    }
  }

  *fetchValuationInfo() {
    this.setLoading(true);

    try {
      const response: ApiResponse = yield this.valuationPaymentService.getValuationInfo(this.token);
      const { data } = response.data;
      this.setValuationInfo(data);
      this.setFee(data.fee);
      this.setCardKnoxUrl(data.cardKnoxUrl);
      this.setPendingPayment(data.pendingPayment);
      this.loanPropertyValuationId = data.loanPropertyValuationId;
      this.setLoading(false);
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: getApiErrorMessage(error, DEFAULT_LINK_ERROR),
      });
      this.setLoading(false);
    }
  }

  reset() {
    this.fee = 0;
    this.token = null;
    this.cardHolderName = '';
    this.loading = true;
    this.valuationInfo = null;
    this.documentSignStatus = false;
    this.documentUrl = null;
    this.cardKnoxURL = null;
    this.paymentStatus = false;
    this.pendingPayment = null;
    this.loanPropertyValuationId = null;
    this.pointOfContact = {
      name: '',
      email: '',
      phoneNumber: '',
      propertyAccessInstructions: '',
    };
    this.savingPointOfContact = false;
  }
}

export default ValuationPaymentStore;
